import {
  BuiltInCalculationLine,
  BuiltInStateConfig,
  getBuiltInStateConfig,
  getRateAsOfDate,
  resolveLineRate,
  TaxBase,
} from './surplusLinesTaxEstimatorConfig';

export type TaxCalculatorInputs = {
  premium: number;
  fees: number;
  policyEffectiveDate?: string;
  endorsementEffectiveDate?: string;
  /** Display / DB name used to apply premium/fee sign rules. */
  transactionTypeName?: string;
};

export type TaxCalculatorLineResult = {
  label: string;
  amount: number;
};

export type TaxCalculatorResult = {
  lines: TaxCalculatorLineResult[];
  total: number;
  taxablePremium: number;
  grandTotal: number;
  config: BuiltInStateConfig;
};

export type TaxValidationError = {
  field: string;
  message: string;
};

export type TaxEstimatorTransactionTypeOption = {
  transactionId: number;
  transactionTypeName: string;
};

/** Premium/fee sign group for Tax Estimator transaction types. */
export type TaxEstimatorSignGroup = 'positive' | 'negative';

type TaxEstimatorTransactionTypeDef = {
  displayName: string;
  /** Normalized (lowercased) names that match a DB TransactionType row. */
  aliases: string[];
  signGroup: TaxEstimatorSignGroup;
};

/**
 * Canonical Tax Estimator transaction types (Kelly feedback / SLIP+ 1–6).
 * Order is the dropdown order.
 */
export const TAX_ESTIMATOR_TRANSACTION_TYPES: readonly TaxEstimatorTransactionTypeDef[] = [
  {
    displayName: 'New Business',
    aliases: ['new business'],
    signGroup: 'positive',
  },
  {
    displayName: 'Additional Premium',
    // Prefer dedicated row; Fall back to FileMule "Endorsement" (SLIP+ maps endorsement → type 2).
    aliases: ['additional premium', 'endorsement'],
    signGroup: 'positive',
  },
  {
    displayName: 'Return Premium',
    // Prefer dedicated row; Fall back to FileMule "Reversal" when Return Premium is absent.
    aliases: ['return premium', 'reversal'],
    signGroup: 'negative',
  },
  {
    displayName: 'Cancellation',
    aliases: ['cancellation', 'flat cancellation'],
    signGroup: 'negative',
  },
  {
    displayName: 'Renewal',
    aliases: ['renewal', 'renewal business'],
    signGroup: 'positive',
  },
  {
    displayName: 'Reinstatement',
    aliases: ['reinstatement'],
    signGroup: 'positive',
  },
];

const normalizeTypeName = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, ' ');

export const getTaxEstimatorSignGroup = (
  transactionTypeName?: string,
): TaxEstimatorSignGroup | null => {
  if (!transactionTypeName) {
    return null;
  }

  const normalized = normalizeTypeName(transactionTypeName);
  const match = TAX_ESTIMATOR_TRANSACTION_TYPES.find((def) =>
    def.aliases.includes(normalized) || normalizeTypeName(def.displayName) === normalized,
  );

  return match?.signGroup ?? null;
};

/**
 * Filter API TransactionType rows to the 6 Tax Estimator types.
 * Matches aliases (e.g. "Renewal Business" → Renewal) and uses Kelly display names.
 */
export const filterTaxEstimatorTransactionTypes = (
  options: TaxEstimatorTransactionTypeOption[],
): TaxEstimatorTransactionTypeOption[] => {
  const byNormalizedName = new Map<string, TaxEstimatorTransactionTypeOption>();
  for (const option of options) {
    const rawName = option?.transactionTypeName;
    const transactionId = Number(option?.transactionId);
    if (
      !rawName
      || !Number.isFinite(transactionId)
      || !Number.isInteger(transactionId)
      || transactionId <= 0
    ) {
      continue;
    }
    byNormalizedName.set(normalizeTypeName(rawName), {
      transactionId,
      transactionTypeName: rawName,
    });
  }

  const filtered: TaxEstimatorTransactionTypeOption[] = [];
  for (const def of TAX_ESTIMATOR_TRANSACTION_TYPES) {
    let matched: TaxEstimatorTransactionTypeOption | undefined;
    for (const alias of def.aliases) {
      matched = byNormalizedName.get(alias);
      if (matched) {
        break;
      }
    }
    if (matched) {
      filtered.push({
        transactionId: matched.transactionId,
        transactionTypeName: def.displayName,
      });
    }
  }

  return filtered;
};

const roundCurrency = (value: number): number => Math.round(value * 100) / 100;

const getBaseAmount = (
  premium: number,
  fees: number,
  base: TaxBase,
): number => (base === 'premium' ? premium : premium + fees);

const calculateLineAmount = (
  premium: number,
  fees: number,
  defaultBase: TaxBase,
  line: BuiltInCalculationLine,
  asOfDate?: string,
): number => {
  if (line.flatAmount != null && line.rate == null && !line.rates?.length) {
    return line.flatAmount;
  }

  const rate = resolveLineRate(line, asOfDate) ?? 0;
  const baseType = line.base ?? defaultBase;
  const baseAmount = getBaseAmount(premium, fees, baseType);
  let amount = baseAmount * rate;

  if (line.roundToNearestDollar) {
    // .50 and greater up; .49 and less down (standard half-up Math.round).
    amount = Math.round(amount);
  } else {
    amount = roundCurrency(amount);
  }

  return amount;
};

export const validateTaxCalculatorInputs = (
  inputs: TaxCalculatorInputs,
): TaxValidationError[] => {
  const errors: TaxValidationError[] = [];

  if (!Number.isFinite(inputs.premium)) {
    errors.push({
      field: 'premium',
      message: 'Premium must be a valid amount.',
    });
  }

  if (!Number.isFinite(inputs.fees)) {
    errors.push({
      field: 'fees',
      message: 'Fees must be a valid amount.',
    });
  }

  if (Number.isFinite(inputs.premium) && Number.isFinite(inputs.fees)) {
    const signGroup = getTaxEstimatorSignGroup(inputs.transactionTypeName);

    if (signGroup === 'positive') {
      // Positive types: premium/fees ≥ 0 (zero allowed; negative = error).
      if (inputs.premium < 0) {
        errors.push({
          field: 'premium',
          message: 'Invalid Premium amount for that Transaction Type.',
        });
      }
      if (inputs.fees < 0) {
        errors.push({
          field: 'fees',
          message: 'Invalid Taxable Fee amount for that Transaction Type.',
        });
      }
    } else if (signGroup === 'negative') {
      // Negative types (Return Premium / Cancellation): premium/fees ≤ 0 (zero allowed).
      if (inputs.premium > 0) {
        errors.push({
          field: 'premium',
          message: 'Invalid Premium amount for that Transaction Type.',
        });
      }
      if (inputs.fees > 0) {
        errors.push({
          field: 'fees',
          message: 'Invalid Taxable Fee amount for that Transaction Type.',
        });
      }
    } else {
      // Unknown / missing type: keep non-negative baseline so a bad type cannot bypass rules.
      if (inputs.premium < 0) {
        errors.push({
          field: 'premium',
          message: 'Premium must be zero or greater.',
        });
      }
      if (inputs.fees < 0) {
        errors.push({
          field: 'fees',
          message: 'Fees must be zero or greater.',
        });
      }
    }
  }

  errors.push(...validateDates(inputs.policyEffectiveDate, inputs.endorsementEffectiveDate));

  return errors;
};

export const validateDates = (
  policyEffectiveDate?: string,
  endorsementEffectiveDate?: string,
): TaxValidationError[] => {
  if (!policyEffectiveDate || !endorsementEffectiveDate) {
    return [];
  }

  const policyDate = new Date(`${policyEffectiveDate}T00:00:00`);
  const endorsementDate = new Date(`${endorsementEffectiveDate}T00:00:00`);

  if (
    !Number.isNaN(policyDate.getTime())
    && !Number.isNaN(endorsementDate.getTime())
    && endorsementDate < policyDate
  ) {
    return [{
      field: 'endorsementEffectiveDate',
      message: 'Transaction effective date cannot be before policy effective date.',
    }];
  }

  return [];
};

export const calculateBuiltInEstimate = (
  stateName: string,
  inputs: TaxCalculatorInputs,
): TaxCalculatorResult | null => {
  const config = getBuiltInStateConfig(stateName);
  if (!config) {
    return null;
  }

  const premium = Number.isFinite(inputs.premium) ? inputs.premium : 0;
  const fees = Number.isFinite(inputs.fees) ? inputs.fees : 0;
  const asOfDate = getRateAsOfDate(config, inputs);

  const lines = config.lines.map((line) => ({
    label: line.label,
    amount: calculateLineAmount(premium, fees, config.defaultBase, line, asOfDate),
  }));

  const total = roundCurrency(lines.reduce((sum, line) => sum + line.amount, 0));
  const taxablePremium = getBaseAmount(premium, fees, config.defaultBase);
  const grandTotal = roundCurrency(premium + fees + total);

  return { lines, total, taxablePremium, grandTotal, config };
};

export const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
